import React, { useState, useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reviewService } from '../../../services/reviewService';
import { useAuth } from '../../../contexts/AuthContext';

const ReviewSubmissionCard = ({ review, userRole, onSubmit }) => {
  const { user } = useAuth();
  const [overallRating, setOverallRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [categoryRatings, setCategoryRatings] = useState({
    communication: 0,
    condition: 0,
    pickup_process: 0,
    value: 0
  });
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const renterCategories = [
    { key: 'communication', label: 'Communication', icon: 'MessageSquare' },
    { key: 'condition', label: 'Trailer Condition', icon: 'CheckCircle' },
    { key: 'pickup_process', label: 'Pickup Process', icon: 'Truck' },
    { key: 'value', label: 'Value for Money', icon: 'DollarSign' }
  ];

  const ownerCategories = [
    { key: 'communication', label: 'Communication', icon: 'MessageSquare' },
    { key: 'condition', label: 'Renter Care', icon: 'CheckCircle' },
    { key: 'pickup_process', label: 'Reliability', icon: 'Clock' },
    { key: 'value', label: 'Overall Behavior', icon: 'ThumbsUp' }
  ];

  const categories = userRole === 'owner' ? ownerCategories : renterCategories;

  const handleCategoryRating = (category, rating) => {
    setCategoryRatings(prev => ({ ...prev, [category]: rating }));
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;

    setUploadingPhotos(true);
    try {
      const newPhotos = [];
      for (const file of files) {
        if (!file?.type?.startsWith('image/')) continue;
        if (file?.size > 10485760) continue;

        const preview = URL.createObjectURL(file);
        newPhotos?.push({
          id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
          file,
          preview,
          name: file?.name,
          uploaded: false,
          publicUrl: null
        });
      }
      setPhotos(prev => [...prev, ...newPhotos]);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleRemovePhoto = (photoId) => {
    setPhotos(prev => prev?.filter(p => p?.id !== photoId));
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      alert('Please provide an overall rating');
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload photos first
      const uploadedUrls = [];
      for (const photo of photos) {
        if (photo?.file && user?.id) {
          const { data, error } = await reviewService?.uploadReviewPhoto(photo?.file, user?.id);
          if (!error && data?.publicUrl) {
            uploadedUrls?.push(data?.publicUrl);
          }
        }
      }

      await onSubmit(review?.id, {
        overallRating,
        categoryRatings,
        comment,
        photos: uploadedUrls
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ratingLabels = { 5: 'Excellent!', 4: 'Very Good', 3: 'Good', 2: 'Fair', 1: 'Poor' };

  const renderStars = (rating, hoverValue, onRate, onHover, onLeave) => (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <button
          key={star}
          onClick={() => onRate(star)}
          onMouseEnter={() => onHover?.(star)}
          onMouseLeave={() => onLeave?.()}
          className="focus:outline-none transition-smooth"
        >
          <Icon
            name="Star"
            size={28}
            strokeWidth={2}
            className={`${star <= (hoverValue || rating) ? 'text-accent fill-accent' : 'text-muted-foreground'} transition-smooth`}
          />
        </button>
      ))}
    </div>
  );

  const renderSmallStars = (rating, onRate) => (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <button
          key={star}
          onClick={() => onRate(star)}
          className="focus:outline-none transition-smooth"
        >
          <Icon
            name="Star"
            size={18}
            strokeWidth={2}
            className={`${star <= rating ? 'text-accent fill-accent' : 'text-muted-foreground'} transition-smooth`}
          />
        </button>
      ))}
    </div>
  );

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      {/* Header */}
      <div className="flex items-start space-x-4 mb-6 pb-6 border-b border-border">
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
          <Image
            src={review?.subjectImage}
            alt={review?.subjectImageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-xl font-semibold text-foreground mb-1">
            Rate {userRole === 'owner' ? 'Renter' : 'Your Experience'}
          </h3>
          <p className="font-body text-foreground mb-2">{review?.subjectName}</p>
          {userRole === 'renter' && (
            <p className="font-caption text-sm text-muted-foreground mb-1">
              Owner: {review?.ownerName}
            </p>
          )}
          <div className="flex items-center space-x-3 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} strokeWidth={2} />
              <span className="font-body">{review?.rentalDates}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} strokeWidth={2} />
              <span className="font-body">{review?.daysAgo} days ago</span>
            </div>
          </div>
        </div>
      </div>
      {/* Overall Rating */}
      <div className="mb-6">
        <label className="font-body font-medium text-foreground mb-3 block">
          Overall Rating *
        </label>
        {renderStars(overallRating, hoverRating, setOverallRating, setHoverRating, () => setHoverRating(0))}
        {overallRating > 0 && (
          <p className="font-caption text-sm text-muted-foreground mt-2">
            {ratingLabels?.[overallRating]}
          </p>
        )}
      </div>
      {/* Category Ratings */}
      <div className="mb-6">
        <label className="font-body font-medium text-foreground mb-3 block">
          Rate Specific Areas
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {categories?.map((category) => (
            <div key={category?.key} className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name={category?.icon} size={16} className="text-primary" strokeWidth={2} />
                <span className="font-body text-sm font-medium text-foreground">
                  {category?.label}
                </span>
              </div>
              {renderSmallStars(
                categoryRatings?.[category?.key],
                (rating) => handleCategoryRating(category?.key, rating)
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Written Review */}
      <div className="mb-6">
        <label className="font-body font-medium text-foreground mb-2 block">
          Share Your Experience
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e?.target?.value)}
          placeholder="Tell others about your experience..."
          rows={4}
          maxLength={500}
          className="w-full px-4 py-3 bg-background border border-input rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <p className="font-caption text-xs text-muted-foreground mt-1">
          {comment?.length}/500 characters
        </p>
      </div>
      {/* Photo Upload */}
      <div className="mb-6">
        <label className="font-body font-medium text-foreground mb-2 block">
          Add Photos (Optional)
        </label>
        <div
          className="border-2 border-dashed border-border rounded-lg p-4 hover:border-primary/50 transition-smooth cursor-pointer"
          onClick={() => fileInputRef?.current?.click()}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Camera" size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <p className="font-body text-sm text-foreground">
                {uploadingPhotos ? 'Processing...' : 'Tap to add photos'}
              </p>
              <p className="font-caption text-xs text-muted-foreground">
                JPG, PNG, WebP (Max 10MB each)
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoSelect}
            className="hidden"
          />
        </div>

        {photos?.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            {photos?.map((photo, index) => (
              <div key={photo?.id} className="relative group aspect-square">
                <div className="w-full h-full rounded-lg overflow-hidden bg-muted border border-border">
                  <img
                    src={photo?.preview}
                    alt={`Review photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => handleRemovePhoto(photo?.id)}
                  className="absolute top-1 right-1 w-6 h-6 bg-error text-error-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth"
                  aria-label="Remove photo"
                >
                  <Icon name="X" size={12} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button variant="ghost" size="sm">
          Skip for Now
        </Button>
        <Button
          variant="default"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={overallRating === 0 || isSubmitting}
          iconName="Send"
        >
          Submit Review
        </Button>
      </div>
    </div>
  );
};

export default ReviewSubmissionCard;